import React, { useState, useEffect, useCallback } from 'react';
import { usersAPI, rolesAPI } from '../../services/api';
import { formatDate } from '../../utils/helpers';
import Badge from '../../components/common/Badge';
import Modal from '../../components/common/Modal';
import Pagination from '../../components/common/Pagination';
import PermissionGuard from '../../components/common/PermissionGuard';
import Spinner from '../../components/common/Spinner';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';

const Users = () => {
  const { user: currentUser } = useAuth();
  const currentIsSuperAdmin = currentUser?.role?.name === 'Super Admin';
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', roleId: '', phone: '' });
  const [saving, setSaving] = useState(false);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await usersAPI.getAll({ page, limit: 10, search });
      setUsers(res.data.data);
      setPagination(res.data.pagination);
    } catch (err) { toast.error('Failed to load users'); }
    finally { setLoading(false); }
  }, [page, search]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  useEffect(() => {
    rolesAPI.getAll().then(res => setRoles(res.data.data)).catch(console.error);
  }, []);

  const openCreate = () => { setEditUser(null); setForm({ name: '', email: '', roleId: '', phone: '' }); setShowModal(true); };
  const openEdit = (user) => {
    setEditUser(user);
    setForm({ name: user.name, email: user.email, roleId: user.role?._id || '', phone: user.phone || '' });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editUser) {
        await usersAPI.update(editUser._id, form);
        toast.success('User updated');
      } else {
        await usersAPI.create(form);
        toast.success('User created! Credentials sent via email.');
      }
      setShowModal(false);
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.message || 'Operation failed'); }
    finally { setSaving(false); }
  };

  const handleToggle = async (id) => {
    try {
      await usersAPI.toggleStatus(id);
      toast.success('Status updated');
      fetchUsers();
    } catch { toast.error('Failed to update status'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this user?')) return;
    try {
      await usersAPI.delete(id);
      toast.success('User deleted');
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.message || 'Delete failed'); }
  };

  const handleResetPw = async (id) => {
    if (!window.confirm('Reset this user\'s password?')) return;
    try {
      await usersAPI.resetPassword(id);
      toast.success('Password reset and emailed to user');
    } catch { toast.error('Failed to reset password'); }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-500 text-sm">{pagination?.total || 0} total users</p>
        </div>
        <div className="flex gap-3">
          <input
            type="text"
            placeholder="Search users..."
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          <PermissionGuard module="users" action="create">
            <button onClick={openCreate} className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 transition">
              + Add User
            </button>
          </PermissionGuard>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-12"><Spinner /></div>
        ) : (
          <>
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr className="text-left text-gray-500 text-xs uppercase tracking-wider">
                  <th className="px-6 py-3 font-medium">User</th>
                  <th className="px-6 py-3 font-medium">Role</th>
                  <th className="px-6 py-3 font-medium">Status</th>
                  <th className="px-6 py-3 font-medium">Last Login</th>
                  <th className="px-6 py-3 font-medium">Created</th>
                  <th className="px-6 py-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {users.map(user => (
                  <tr key={user._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-700 font-medium">
                          {user.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{user.name}</p>
                          <p className="text-gray-500 text-xs">{user.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs">{user.role?.name || '—'}</span>
                    </td>
                    <td className="px-6 py-4">
                      <Badge status={user.isActive ? 'active' : 'inactive'} />
                    </td>
                    <td className="px-6 py-4 text-gray-500">{formatDate(user.lastLogin)}</td>
                    <td className="px-6 py-4 text-gray-500">{formatDate(user.createdAt)}</td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        <PermissionGuard module="users" action="update">
                          <button onClick={() => openEdit(user)} className="text-indigo-600 hover:underline text-xs">Edit</button>
                          <button onClick={() => handleToggle(user._id)} className="text-yellow-600 hover:underline text-xs">
                            {user.isActive ? 'Deactivate' : 'Activate'}
                          </button>
                          <button onClick={() => handleResetPw(user._id)} className="text-blue-600 hover:underline text-xs">Reset PW</button>
                        </PermissionGuard>
                        <PermissionGuard module="users" action="delete">
                          <button onClick={() => handleDelete(user._id)} className="text-red-600 hover:underline text-xs">Delete</button>
                        </PermissionGuard>
                      </div>
                    </td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr><td colSpan={6} className="px-6 py-12 text-center text-gray-400">No users found</td></tr>
                )}
              </tbody>
            </table>
            <Pagination pagination={pagination} onPageChange={setPage} />
          </>
        )}
      </div>

      {/* Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editUser ? 'Edit User' : 'Create User'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input type="text" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
            <select value={form.roleId} onChange={e => setForm({...form, roleId: e.target.value})} required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500">
              <option value="">Select Role</option>
              {roles
                .filter(r => currentIsSuperAdmin || r.name !== 'Super Admin')
                .map(r => <option key={r._id} value={r._id}>{r.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone (optional)</label>
            <input type="text" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500" />
          </div>
          {!editUser && (
            <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg text-sm text-blue-700">
              A temporary password will be auto-generated and sent to the user's email.
            </div>
          )}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={() => setShowModal(false)}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={saving}
              className="flex-1 bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-60">
              {saving ? 'Saving...' : (editUser ? 'Update' : 'Create User')}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Users;
